import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { CheckCircle, Package } from 'lucide-react';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [orderDetails, setOrderDetails] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    clearCart();
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          // Fetch order details
          try {
            const { data: details } = await api.get(`/checkout/order/${sessionId}`);
            setOrderDetails(details);
          } catch {}
          return;
        }
        if (data.status === 'expired' || data.status === 'failed') { setStatus('failed'); return; }
        setTimeout(poll, 2000);
      } catch { setStatus('error'); }
    };
    poll();
  }, [sessionId, clearCart]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: 'var(--bg-color)', color: 'var(--text-color)' }}>
      <div className="text-center max-w-lg w-full">
        {status === 'checking' && (
          <div className="mb-4" style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>Confirming your payment...</div>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle size={48} style={{ color: 'var(--accent-color)', margin: '0 auto 1.5rem' }} />
            <h1 data-testid="order-confirmed-heading" style={{ fontFamily: 'var(--headline-font)', fontStyle: 'italic', fontSize: 'clamp(1.8rem, 4vw, 3rem)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              Order Confirmed
            </h1>
            {orderDetails && (
              <div data-testid="order-details" className="text-left mt-6 mx-auto" style={{ maxWidth: '24rem' }}>
                <div className="flex items-center gap-2 mb-4 pb-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <Package size={16} style={{ color: 'var(--accent-color)' }} />
                  <span data-testid="order-number" style={{ fontFamily: 'var(--body-font)', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}>
                    Order #{orderDetails.order_number}
                  </span>
                </div>
                {/* Items */}
                <div className="space-y-2 mb-4">
                  {orderDetails.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between" style={{ fontFamily: 'var(--body-font)', fontSize: '0.8rem' }}>
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p style={{ fontSize: '0.7rem', color: 'var(--muted-color)' }}>
                          {item.variant_name?.split(' / ').slice(1).join(' / ')} x{item.quantity}
                        </p>
                      </div>
                      <span className="font-semibold">${(parseFloat(item.retail_price) * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                {/* Totals */}
                <div className="pt-3 space-y-1" style={{ borderTop: '1px solid var(--border-color)', fontFamily: 'var(--body-font)', fontSize: '0.8rem' }}>
                  <div className="flex justify-between" style={{ color: 'var(--muted-color)' }}>
                    <span>Subtotal</span>
                    <span>${parseFloat(orderDetails.total_amount).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between" style={{ color: 'var(--muted-color)' }}>
                    <span>Shipping</span>
                    <span>${parseFloat(orderDetails.shipping_amount).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold pt-1">
                    <span>Total</span>
                    <span>${(parseFloat(orderDetails.total_amount) + parseFloat(orderDetails.shipping_amount)).toFixed(2)}</span>
                  </div>
                </div>
                {/* Shipping */}
                {orderDetails.shipping_address?.name && (
                  <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
                    <p style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', color: 'var(--muted-color)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>Shipping to</p>
                    <p style={{ fontFamily: 'var(--body-font)', fontSize: '0.8rem' }}>
                      {orderDetails.shipping_address.name}
                      {orderDetails.shipping_address.city && `, ${orderDetails.shipping_address.city}`}
                      {orderDetails.shipping_address.state && `, ${orderDetails.shipping_address.state}`}
                    </p>
                  </div>
                )}
                {orderDetails.customer_email && (
                  <p className="mt-3" style={{ fontFamily: 'var(--body-font)', fontSize: '0.75rem', color: 'var(--muted-color)' }}>
                    Confirmation sent to {orderDetails.customer_email}
                  </p>
                )}
              </div>
            )}
            <p className="mt-6" style={{ fontFamily: 'var(--body-font)', fontSize: '0.85rem', color: 'var(--muted-color)', lineHeight: 1.7 }}>
              Your items will ship in 5–10 business days.
            </p>
            <Link to="/" className="mt-6 inline-block" style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--accent-color)', textDecoration: 'none' }}>
              Back to the Issue
            </Link>
          </>
        )}
        {(status === 'failed' || status === 'error') && (
          <>
            <h1 style={{ fontFamily: 'var(--headline-font)', fontStyle: 'italic', fontSize: 'clamp(1.8rem, 4vw, 3rem)', textTransform: 'uppercase', marginBottom: '1rem' }}>Something Went Wrong</h1>
            <p style={{ fontFamily: 'var(--body-font)', fontSize: '0.9rem', color: 'var(--muted-color)', marginBottom: '2rem' }}>
              We couldn't confirm your payment. Please contact us if you were charged.
            </p>
            <Link to="/" style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--accent-color)', textDecoration: 'none' }}>Back to the Issue</Link>
          </>
        )}
      </div>
    </div>
  );
}
